import { gsap } from "gsap";
import SplitType from "split-type";
import { useRefs } from "../framework";

export const text = (ref, { signal } = { signal: null }) => {
  if (!ref.animateText) return;

  // save all instances to be able to revert/kill them
  let gsaps = [];
  let splits = [];

  const observer = new IntersectionObserver(callback, { threshold: 1 });

  ref.animateText.forEach((element) => {
    element.style.opacity = 0;
    const split = new SplitType(element, { types: "lines,words" });
    split.lines.forEach((line) => (line.style.overflowY = "clip"));
    splits.push(split);
    observer.observe(element);
  });

  function callback(entries: IntersectionObserverEntry[]) {
    entries.forEach(async (entry) => {
      if (!entry.isIntersecting) return;

      const target = entry.target as HTMLElement;

      const words = Array.from(target.querySelectorAll(".word"));

      setTimeout(() => {
        target.style.opacity = "1";
      });

      await Promise.all(
        words.map(async (word, idx) => {
          (word as HTMLElement).style.opacity = "0";

          return word.animate(
            [
              { transform: "translateY(75%)", opacity: 0 },
              { transform: "translateY(-0.0em)", opacity: 1 },
            ],
            {
              duration: 1000,
              easing: "cubic-bezier(0.25, 1, 0.5, 1)",
              delay: 30 * idx,
              fill: "forwards",
            },
          ).finished;
        }),
      );

      splits.forEach((split) =>
        split.lines?.forEach((line) => line.style.removeProperty("overflow-y")),
      );

      observer.unobserve(entry.target);
    });
  }

  window.addEventListener(
    "resize",
    () => {
      splits.forEach((item) => item.revert());
      // gsaps.forEach((item) => item.revert());
    },
    { signal },
  );

  return () => {
    observer.disconnect();
  };
};

export const items = (ref) => {
  if (!ref.animateItems) return;

  /**
   * usage:
   * <div data-ref="animate-items">
   *  <div data-ref-animate-items="items"></div>
   *  <div data-ref-animate-items="items"></div>
   *  <div data-ref-animate-items="items"></div>
   * </div>
   */

  function callback(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      const { stagger } = entry.target.dataset;
      entry.target.style.opacity = 1;

      const refs = useRefs({
        root: entry.target,
        namespaced: true,
        asArray: true,
      });

      if (refs.items) {
        gsap.from(refs.items, {
          y: "20px",
          opacity: 0,
          delay: 0.2,
          stagger: parseFloat(stagger || 0.05),
          duration: 1,
          ease: "power3.out",
        });
      }

      observer.unobserve(entry.target);
    });
  }

  const observer = new IntersectionObserver(callback, { threshold: 0 });

  ref.animateItems.forEach((parent) => {
    parent.style.opacity = 0;
    observer.observe(parent);
  });

  return () => {
    observer.disconnect();
  };
};

export const fadeIn = (ref) => {
  if (!ref.animateFadeIn) return;

  function callback(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      const { delay } = entry.target.dataset;

      gsap.to(entry.target, {
        opacity: 1,
        y: 0,
        duration: 1,
        delay: parseFloat(delay || 0.2),
        ease: "power3.out",
      });

      observer.unobserve(entry.target);
    });
  }

  const observer = new IntersectionObserver(callback, { threshold: 0 });

  ref.animateFadeIn.forEach((element) => {
    const { noMovement } = element.dataset;
    element.style.opacity = 0;
    element.style.transform =
      noMovement === undefined ? `translateY(20px)` : null;
    observer.observe(element);
  });

  return () => {
    observer.disconnect();
  };
};
